import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, DollarSign, Search, Receipt, Building, Ticket, CheckCircle, Clock, Heart } from 'lucide-react';
import { getErrorMessage } from '@/utils/errorHandler';
import { formatDateDisplay } from '@/utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerEvents() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('events');
  const [customerApproved, setCustomerApproved] = useState(true);
  
  // Events state
  const [events, setEvents] = useState([]);
  const [eventSearch, setEventSearch] = useState('');
  const [eventMemberships, setEventMemberships] = useState({});
  
  // Business state
  const [businesses, setBusinesses] = useState([]);
  const [businessSearch, setBusinessSearch] = useState('');
  const [businessMemberships, setBusinessMemberships] = useState({});
  
  // Wishlist state
  const [wishlist, setWishlist] = useState([]);

  // System settings
  const [showReceiptImageUpload, setShowReceiptImageUpload] = useState(true);


  // Check customer approval status
  useEffect(() => {
    const checkApprovalStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomerApproved(response.data.approval_status === 'approved');
      } catch (error) {
        console.error('Error checking approval status:', error);
      }
    };
    checkApprovalStatus();
    fetchSystemSettings();
  }, []);

  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/settings`);
      setShowReceiptImageUpload(response.data.show_receipt_image_upload !== false);
    } catch (error) {
      console.error('Error fetching system settings:', error);
      // Default to showing image upload if fetch fails
      setShowReceiptImageUpload(true);
    }
  };

  // Fetch events
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchEvents();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [eventSearch]);

  // Fetch businesses
  useEffect(() => {
    fetchBusinesses();
    fetchWishlist();
  }, []);
  
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (activeTab === 'businesses') {
        fetchBusinesses();
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [businessSearch, activeTab]);

  const fetchWishlist = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(response.data.map(e => e.event_id));
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    }
  };

  const handleToggleWishlist = async (eventId, e) => {
    e.stopPropagation(); // Prevent card click
    try {
      const token = localStorage.getItem('token');
      if (wishlist.includes(eventId)) {
        await axios.delete(`${API}/wishlist/remove/${eventId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlist(wishlist.filter(id => id !== eventId));
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/add/${eventId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlist([...wishlist, eventId]);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events/active?search=${eventSearch}`);
      const eventsData = response.data;
      setEvents(eventsData);
      
      // Fetch membership status for each event AND business membership
      const token = localStorage.getItem('token');
      const membershipPromises = (eventsData || []).map(async (event) => {
        try {
          // Get event membership status
          const eventMembershipRes = await axios.get(`${API}/events/${event.event_id}/membership-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Get business membership status for this event's business
          const businessMembershipRes = await axios.get(`${API}/businesses/${event.business_id}/membership-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          return {
            eventId: event.event_id,
            eventStatus: eventMembershipRes.data.status,
            businessStatus: businessMembershipRes.data.status
          };
        } catch (error) {
          return {
            eventId: event.event_id,
            eventStatus: 'not_member',
            businessStatus: 'not_member'
          };
        }
      });
      
      const memberships = await Promise.all(membershipPromises);
      const membershipMap = {};
      memberships.forEach(m => {
        membershipMap[m.eventId] = {
          eventStatus: m.eventStatus,
          businessStatus: m.businessStatus
        };
      });
      setEventMemberships(membershipMap);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to fetch events');
    }
  };

  const fetchBusinesses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/businesses/search?query=${businessSearch}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const businessesData = response.data;
      setBusinesses(businessesData);
      
      // Fetch membership status for each business
      const membershipPromises = (businessesData || []).map(business =>
        axios.get(`${API}/businesses/${business.business_id}/membership-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => ({ businessId: business.business_id, status: res.data.status }))
        .catch(() => ({ businessId: business.business_id, status: 'not_member' }))
      );
      
      const memberships = await Promise.all(membershipPromises);
      const membershipMap = {};
      memberships.forEach(m => {
        membershipMap[m.businessId] = m.status;
      });
      setBusinessMemberships(membershipMap);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      toast.error('Failed to fetch businesses');
    }
  };

  const handleJoinBusiness = async (businessId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/businesses/join?business_id=${businessId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Join request sent to business!');
      fetchBusinesses();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send join request'));
    }
  };

  const handleJoinEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/events/join?event_id=${eventId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Join request sent for event!');
      fetchEvents();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send join request'));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and PDF files are allowed');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/receipts/upload/${selectedEvent.event_id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setReceiptForm({ ...receiptForm, receipt_image_url: response.data.image_url });
      setImagePreview(URL.createObjectURL(file));
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmitReceipt = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/receipts/submit`,
        { ...receiptForm, event_id: selectedEvent.event_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Receipt submitted successfully!');
      setShowSubmitDialog(false);
      setSelectedEvent(null);
      setReceiptForm({
        receipt_number: '',
        transaction_amount: '',
        transaction_date: '',
        business_name: '',
        person_helped: '',
        receipt_image_url: ''
      });
      setImagePreview(null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit receipt'));
    }
  };

  return (
    <div className="min-h-screen bg-cream pt-4 sm:pt-6 pb-12 px-4 sm:px-0" data-testid="customer-events-page">
      <div className="max-w-7xl mx-auto sm:px-4 lg:px-8">
        {/* Header - Responsive */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-playfair font-bold text-3xl sm:text-4xl text-primary mb-2">Discover & Join</h1>
            <p className="font-outfit text-sm sm:text-base text-gray-600">Browse businesses and events to participate</p>
          </div>
          
          {/* Action Buttons - Responsive */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => navigate('/customer/joined-businesses')}
              variant="outline"
              size="sm"
              data-testid="joined-businesses-button"
              className="text-xs sm:text-sm"
            >
              <Building className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Joined Businesses</span>
              <span className="sm:hidden">Businesses</span>
            </Button>
            <Button
              onClick={() => navigate('/customer/joined-events')}
              variant="outline"
              size="sm"
              data-testid="joined-events-button"
              className="text-xs sm:text-sm"
            >
              <Ticket className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Joined Events</span>
              <span className="sm:hidden">Events</span>
            </Button>
            <Button
              onClick={() => navigate('/customer/receipts')}
              variant="outline"
              size="sm"
              data-testid="my-receipts-button"
              className="text-xs sm:text-sm"
            >
              <Receipt className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">My Receipts</span>
              <span className="sm:hidden">Receipts</span>
            </Button>
            <Button
              onClick={() => navigate('/customer/coupons')}
              className="bg-amber-600 hover:bg-amber-700 text-xs sm:text-sm"
              size="sm"
              data-testid="my-coupons-button"
            >
              <span className="hidden sm:inline">My Credits</span>
              <span className="sm:hidden">Coupons</span>
            </Button>
          </div>
        </div>

        {/* Approval Status Warning */}
        {!customerApproved && (
          <Card className="p-3 sm:p-4 mb-4 sm:mb-6 bg-yellow-50 border-yellow-200">
            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 flex-shrink-0 mt-0.5 sm:mt-0" />
              <div className="flex-1 min-w-0">
                <p className="font-outfit font-semibold text-sm sm:text-base text-yellow-800">Account Pending Approval</p>
                <p className="font-outfit text-xs sm:text-sm text-yellow-700">
                  Your account is awaiting admin approval. You can join businesses, but you won't be able to join events until your account is approved.
                </p>
              </div>
            </div>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-4 sm:mb-6">
            <TabsTrigger value="events" className="font-outfit text-sm">
              <Ticket className="w-4 h-4 mr-2" />
              Events
            </TabsTrigger>
            <TabsTrigger value="businesses" className="font-outfit text-sm">
              <Building className="w-4 h-4 mr-2" />
              Businesses
            </TabsTrigger>
          </TabsList>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="mb-4 sm:mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <Input
                  placeholder="Search events..."
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  className="pl-10 text-sm sm:text-base"
                  data-testid="event-search-input"
                />
              </div>
            </div>

            {(events || []).length === 0 ? (
              <Card className="p-8 sm:p-12 text-center">
                <p className="font-outfit text-sm sm:text-base text-gray-600">No events found</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {(events || []).map((event) => {
                  const membership = eventMemberships[event.event_id] || { eventStatus: 'not_member', businessStatus: 'not_member' };
                  const membershipStatus = membership.eventStatus;
                  const businessMembershipStatus = membership.businessStatus;
                  
                  return (
                    <Card key={event.event_id} className="p-4 sm:p-6 hover:shadow-lg transition-shadow relative" data-testid={`event-card-${event.event_id}`}>
                      <Button
                        onClick={(e) => handleToggleWishlist(event.event_id, e)}
                        variant="ghost"
                        size="sm"
                        className="absolute top-4 right-4 z-10"
                      >
                        <Heart 
                          className={`w-5 h-5 ${wishlist.includes(event.event_id) ? 'fill-red-500 text-red-500' : 'text-gray-400'}`}
                        />
                      </Button>
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2 pr-10">
                        <h3 className="font-playfair font-bold text-xl sm:text-2xl text-primary break-words">{event.event_name}</h3>
                        <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded self-start">{event.event_id}</span>
                      </div>
                      <p className="font-outfit text-xs sm:text-sm text-gray-500 mb-2 break-words">By {event.business_name}</p>
                      <p className="font-outfit text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-3">{event.event_description}</p>
                      
                      <div className="space-y-2 mb-3 sm:mb-4">
                        {/* Only show coupon value if not hidden by business */}
                        {!event.coupon_value_hidden && (
                          <div className="flex items-center text-xs sm:text-sm">
                            <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600 mr-2 flex-shrink-0" />
                            <span className="font-outfit">Incentive Points: {event.coupon_value}</span>
                          </div>
                        )}
                        <div className="flex items-center text-xs sm:text-sm">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600 mr-2 flex-shrink-0" />
                          <span className="font-outfit">Ends: {formatDateDisplay(event.end_date)}</span>
                        </div>
                      </div>

                      <div className="border-t pt-3 sm:pt-4 mb-3 sm:mb-4">
                        <p className="font-outfit font-semibold text-xs sm:text-sm text-gray-700 mb-2">Prizes:</p>
                        <ul className="space-y-1">
                          {(event.prizes || []).map((prize, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm">
                              <span className="text-amber-600 mt-0.5 flex-shrink-0">•</span>
                              <div className="flex-1 min-w-0">
                                <span className="font-semibold text-gray-800 break-words">{prize.prize_name || `Prize ${idx + 1}`}</span>
                                {prize.prize_description && (
                                  <span className="text-gray-600 break-words"> - {prize.prize_description}</span>
                                )}
                                <span className="text-amber-600 font-semibold ml-2">${prize.prize_amount}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {membershipStatus === 'approved' ? (
                        <div className="space-y-2">
                          <div className="flex items-center text-sm text-green-600 font-outfit mb-2">
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Joined
                          </div>
                          <Button
                            onClick={() => navigate('/customer/joined-events')}
                            className="w-full bg-primary hover:bg-primary/90"
                            data-testid="view-joined-event-button"
                          >
                            View in My Events
                          </Button>
                        </div>
                      ) : membershipStatus === 'pending' ? (
                        <Button
                          disabled
                          className="w-full"
                          variant="outline"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Pending Approval
                        </Button>
                      ) : (() => {
                        // Add 1-day grace period: event expires 1 day AFTER end_date
                        const eventEndDate = new Date(event.end_date);
                        const gracePeriodDate = new Date(eventEndDate.getTime() + 24 * 60 * 60 * 1000);
                        return gracePeriodDate < new Date();
                      })() ? (
                        <Button
                          disabled
                          className="w-full"
                          variant="outline"
                        >
                          Event Expired
                        </Button>
                      ) : !customerApproved ? (
                        <Button
                          disabled
                          className="w-full"
                          variant="outline"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Approval Required
                        </Button>
                      ) : businessMembershipStatus !== 'approved' ? (
                        <Button
                          disabled
                          className="w-full"
                          variant="outline"
                        >
                          <Building className="w-4 h-4 mr-2" />
                          Join Business First
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleJoinEvent(event.event_id)}
                          className="w-full bg-amber-600 hover:bg-amber-700"
                          data-testid="join-event-button"
                        >
                          Join Event
                        </Button>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Businesses Tab */}
          <TabsContent value="businesses">
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search businesses..."
                  value={businessSearch}
                  onChange={(e) => setBusinessSearch(e.target.value)}
                  className="pl-10"
                  data-testid="business-search-input"
                />
              </div>
            </div>

            {(businesses || []).length === 0 ? (
              <Card className="p-12 text-center">
                <p className="font-outfit text-gray-600">No businesses found</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(businesses || []).map((business) => {
                  const membershipStatus = businessMemberships[business.business_id] || 'not_member';
                  return (
                    <Card key={business.business_id} className="p-6 hover:shadow-lg transition-shadow" data-testid={`business-card-${business.business_id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-playfair font-bold text-2xl text-primary">{business.business_name}</h3>
                        <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{business.business_id}</span>
                      </div>
                      <p className="font-outfit text-sm text-gray-500 mb-2">{business.business_type}</p>
                      <p className="font-outfit text-gray-600 text-sm mb-4">
                        {business.business_description || 'No description available'}
                      </p>

                      <div className="border-t pt-4 mb-4">
                        <p className="font-outfit text-sm text-gray-600">
                          <span className="font-semibold">Owner:</span> {business.owner_name}
                        </p>
                        {business.address && (
                          <p className="font-outfit text-sm text-gray-600 mt-1">
                            <span className="font-semibold">Address:</span> {business.address}
                          </p>
                        )}
                      </div>

                      {membershipStatus === 'approved' ? (
                        <Button
                          disabled
                          className="w-full"
                          variant="outline"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Joined
                        </Button>
                      ) : membershipStatus === 'pending' ? (
                        <Button
                          disabled
                          className="w-full"
                          variant="outline"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Pending Approval
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleJoinBusiness(business.business_id)}
                          className="w-full bg-amber-600 hover:bg-amber-700"
                          data-testid="join-business-button"
                        >
                          Join Business
                        </Button>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}